import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui'
import { marketingAPI } from '../services/api'
import { useReportLayout } from '../hooks/useReportLayout'
import { ChannelColumnPicker } from './ChannelReportTable'
import ChannelViewPicker from './ChannelViewPicker'
import AttributionModelDetails from './AttributionModelDetails'
import AttributionWindowControl, { AttributionWindowExplanation } from './AttributionWindowControl'
import { ATTRIBUTION_CHANNEL_LABELS } from './AttributionCreditTable'
import { CpmrSparkline, QualitySparkline } from './Sparklines'
import { channelColor, channelLabel } from '../lib/channels'
import { formatChannelMetric } from '../lib/channelReport'
import {
  CAMPAIGN_COLUMNS, DEFAULT_CAMPAIGN_COLUMNS, mergeCampaignCredit, normalizeCampaignColumns,
  sortCampaignRows, withCampaignMetrics,
} from '../lib/campaignReport'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'

const COLUMN_STORAGE_KEY = 'campaign-report-columns:v1'
const VIEW_STORAGE_KEY = 'campaign-report-view:v1'
const controlClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground'
const fieldClass = 'flex flex-col gap-1 text-xs text-muted-foreground'
// Shown until the endpoint names its own models.
const FALLBACK_MODELS = [{ id: 'last-non-direct', label: 'Last Non-Direct Touch' }]
// Same calendar strings the spend tree is requested with, so both cover one window.
const dayOf = date => format(date, 'yyyy-MM-dd')

function useCampaignCredit({ enabled, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode }) {
  const key = JSON.stringify([enabled, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode])
  const [state, setState] = useState({ key: null, data: null, error: null })
  useEffect(() => {
    if (!enabled) return
    let active = true
    marketingAPI.getCampaignAttribution({ startDate: start, endDate: end, engine, scope, lookbackDays, attributionWindow, accountingMode })
      .then(response => { if (active) setState({ key, data: response.data.data, error: null }) })
      .catch(error => { if (active) setState({ key, data: null, error: error.response?.data?.error?.message || 'Could not load attributed sales.' }) })
    return () => { active = false }
  }, [key, enabled, start, end, engine, scope, lookbackDays, attributionWindow, accountingMode])
  if (!enabled) return { data: null, error: null, loading: false }
  return state.key === key ? { ...state, loading: false } : { data: null, error: null, loading: true }
}

/** A metric's rendered value, shared by the table cells and the phone cards. */
function metricValue(row, column, level, adQuality, pending) {
  if (column.attributed && pending) return { content: '…', muted: true }
  if (column.key === 'quality') {
    const series = level === 'ad' ? adQuality?.[row.ad_name] : null
    const scored = (series || []).filter(value => value !== null && value !== undefined)
    if (!scored.length) return { content: null, muted: true }
    const latest = scored[scored.length - 1]
    return { content: <span className="inline-flex items-center justify-end gap-1.5" title={`Traffic quality ${latest.toFixed(2)} (expected conversion %)`}>
      <span>{latest.toFixed(2)}</span><QualitySparkline data={series} />
    </span> }
  }
  if (column.key === 'cpmr') {
    if (row.cpmr == null) return { content: '—', muted: true }
    return { content: <span className="inline-flex items-center justify-end gap-1.5">
      <span title={row.frequency ? `Freq: ${row.frequency.toFixed(1)}x` : undefined}>{formatChannelMetric(row.cpmr, 'money2')}</span>
      {level === 'ad' && <CpmrSparkline data={row.cpmr_daily} />}
    </span> }
  }
  const value = row[column.key]
  return { content: formatChannelMetric(value, column.type), muted: value == null }
}

function MetricCell({ row, column, level, adQuality, pending }) {
  const { content, muted } = metricValue(row, column, level, adQuality, pending)
  return <td className={`px-3 py-2.5 text-right align-top whitespace-nowrap font-mono ${level === 'ad' ? 'text-xs' : 'text-sm'} ${muted ? 'text-muted-foreground' : ''}`}>{content}</td>
}

/** Metrics shown on a phone card before "Show all": the first of the user's own columns. */
const HEADLINE_COUNT = 4

function MetricList({ row, columns, level, adQuality, pending, layout }) {
  if (layout === 'grid') {
    return <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
      {columns.map(column => {
        const { content, muted } = metricValue(row, column, level, adQuality, pending)
        return <div key={column.key} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{column.label}</dt>
          <dd className={`font-mono tabular-nums ${level === 'campaign' ? 'text-base font-semibold' : 'text-sm'} ${muted ? 'text-muted-foreground' : ''}`}>{content ?? '—'}</dd>
        </div>
      })}
    </dl>
  }
  return <dl className="divide-y divide-border/50">
    {columns.map(column => {
      const { content, muted } = metricValue(row, column, level, adQuality, pending)
      return <div key={column.key} className="flex items-baseline justify-between gap-3 py-1">
        <dt className="text-xs text-muted-foreground">{column.label}</dt>
        <dd className={`font-mono text-sm tabular-nums ${muted ? 'text-muted-foreground' : ''}`}>{content ?? '—'}</dd>
      </div>
    })}
  </dl>
}

/**
 * One row of the hierarchy as a phone card: name, channel, the first few of the
 * user's columns, every column behind "Show all", and its children behind a
 * second toggle. The table is hidden on phones rather than squeezed: a pinned
 * 256px name column left no room for a single number.
 */
function MobileRow({ row, level, name, channel, columns, adQuality, pending, childLabel, children, tone = 'card' }) {
  const [showAll, setShowAll] = useState(false)
  const [open, setOpen] = useState(false)
  const headline = columns.slice(0, HEADLINE_COUNT)
  const rest = columns.slice(HEADLINE_COUNT)
  const childCount = Array.isArray(children) ? children.length : 0
  const toggleClass = 'flex w-full items-center justify-center gap-1 border-t border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50'
  return <div className={`rounded-lg border ${tone === 'total' ? 'bg-muted' : level === 'campaign' ? 'bg-card' : 'bg-muted/20'}`}>
    <div className="space-y-2 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className={`min-w-0 break-words ${level === 'campaign' ? 'text-sm font-semibold' : level === 'ad' ? 'text-xs' : 'text-sm'}`}>{name}</div>
        {channel && <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2 rounded-sm" style={{ backgroundColor: channelColor(channel) }} />{ATTRIBUTION_CHANNEL_LABELS[channel] || channelLabel(channel)}</span>}
      </div>
      <MetricList row={row} columns={headline} level={level} adQuality={adQuality} pending={pending} layout="grid" />
    </div>
    {rest.length > 0 && <>
      <button type="button" onClick={() => setShowAll(value => !value)} aria-expanded={showAll} className={toggleClass}>
        {showAll ? 'Hide' : `Show all ${columns.length}`}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
      </button>
      {showAll && <div className="border-t border-border/60 px-3 pb-2"><MetricList row={row} columns={rest} level={level} adQuality={adQuality} pending={pending} /></div>}
    </>}
    {childCount > 0 && <>
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className={toggleClass}>
        {open ? `Hide ${childLabel}` : `${childCount} ${childLabel}`}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-2 border-t border-border/60 p-2">{children}</div>}
    </>}
  </div>
}

function NameCell({ depth, expandable, expanded, onToggle, children, muted }) {
  const indent = ['pl-3', 'pl-8', 'pl-14'][depth]
  const Chevron = expanded ? ChevronDown : ChevronRight
  return <th scope="row" className={`sticky left-0 z-10 bg-background group-hover:bg-muted text-left font-normal align-top py-2.5 pr-3 ${indent} min-w-64 max-w-md`}>
    <span className="flex items-start gap-1.5">
      {expandable
        ? <button type="button" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? 'Collapse' : 'Expand'} className="mt-0.5 rounded hover:bg-muted"><Chevron className="h-4 w-4 text-muted-foreground" /></button>
        : <span className="w-4 shrink-0" />}
      <span className={`min-w-0 break-words ${muted ? 'text-muted-foreground' : ''} ${depth === 0 ? 'font-medium' : depth === 2 ? 'text-xs' : 'text-sm'}`}>{children}</span>
    </span>
  </th>
}

// Colour from the app-wide palette (lib/channels), never the API's own map.
function ChannelCell({ channel }) {
  if (!channel) return <td className="px-3 py-2.5" />
  const color = channelColor(channel)
  return <td className="px-3 py-2.5 align-top whitespace-nowrap">
    <span className="inline-flex items-center gap-1.5 text-xs"><span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />{ATTRIBUTION_CHANNEL_LABELS[channel] || channelLabel(channel)}</span>
  </td>
}

const NoSpend = () => <span className="ml-1.5 text-[11px] text-muted-foreground" title="Credited from a click inside the lookback; no spend in these dates.">· no spend in dates</span>

export default function CampaignBreakdown({ tree, adQuality, dateRange, clinicId }) {
  const supportsModels = Boolean(clinicId)
  const [engine, setEngine] = useState('last-non-direct')
  const [scope, setScope] = useState('new')
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page
  const [attributionWindow, setAttributionWindow] = useState('all')
  const [accountingMode, setAccountingMode] = useState('payment')
  const [channelFilter, setChannelFilter] = useState('')
  const [expanded, setExpanded] = useState(() => new Set())
  const [sort, setSort] = useState({ key: 'spend', direction: 'desc' })
  const [preferences, setPreferences] = useReportLayout('campaigns', normalizeCampaignColumns, COLUMN_STORAGE_KEY)
  const [activeViewId, setActiveViewId] = useState(() => { try { return localStorage.getItem(VIEW_STORAGE_KEY) } catch { return null } })
  useEffect(() => {
    try { activeViewId ? localStorage.setItem(VIEW_STORAGE_KEY, activeViewId) : localStorage.removeItem(VIEW_STORAGE_KEY) } catch { /* Storage may be disabled. */ }
  }, [activeViewId])
  // A new window is a new tree; old expansion keys would point at nothing.
  useEffect(() => { setExpanded(new Set()) }, [tree])

  const credit = useCampaignCredit({ enabled: supportsModels, start: dayOf(dateRange.from), end: dayOf(dateRange.to),
    engine, scope, lookbackDays, attributionWindow, accountingMode })
  const pending = supportsModels && credit.loading

  // Attributed columns are offered only where the engine runs.
  const catalog = supportsModels ? CAMPAIGN_COLUMNS : CAMPAIGN_COLUMNS.filter(column => !column.attributed)
  const columns = preferences.order.map(key => catalog.find(column => column.key === key))
    .filter(column => column && preferences.visible.includes(column.key))
  const visibleColumns = columns.length ? columns : [catalog[0]]

  const campaigns = useMemo(() => mergeCampaignCredit(tree, credit.data), [tree, credit.data])
  const channels = [...new Set(campaigns.map(campaign => campaign.channel))].sort()
  const shown = sortCampaignRows(campaigns.filter(campaign => !channelFilter || campaign.channel === channelFilter), sort)
  const unmatched = (credit.data?.unmatched || []).filter(row => !channelFilter || row.channel === channelFilter)
    .map(row => withCampaignMetrics({ ...row, spend: null, clicks: null, impressions: null }, null))
  const totals = withCampaignMetrics([...shown, ...unmatched].reduce((sum, row) => {
    for (const key of ['spend', 'clicks', 'impressions', 'orders', 'revenue', 'new_orders']) {
      if (row[key] != null) sum[key] = (sum[key] ?? 0) + row[key]
    }
    return sum
  }, {}), tree?.summary?.total_spend)
  if (supportsModels && !credit.data) Object.assign(totals, { orders: null, revenue: null, new_orders: null, cac: null, roas: null, aov: null, profit: null, click_to_sale: null })

  const toggle = key => setExpanded(previous => {
    const next = new Set(previous)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })
  const toggleSort = key => setSort(previous => ({ key, direction: previous.key === key && previous.direction === 'desc' ? 'asc' : 'desc' }))
  const models = credit.data?.models || FALLBACK_MODELS
  const overlapping = credit.data?.model && !credit.data.model.reconciles
  const placedShare = credit.data?.totals?.revenue > 0 ? credit.data.totals.matched_revenue / credit.data.totals.revenue * 100 : null

  const header = (key, label, align, definition) => <th key={key} scope="col"
    aria-sort={sort.key === key ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}
    className={`bg-muted px-3 py-2.5 text-${align} text-xs font-medium whitespace-nowrap ${key === 'name' ? 'sticky left-0 z-20' : ''}`}>
    <span className="group/definition relative inline-flex">
      <button type="button" onClick={() => toggleSort(key)} className={`inline-flex items-center gap-1 hover:text-primary ${definition ? 'cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4' : ''}`} aria-describedby={definition ? `campaign-definition-${key}` : undefined}>
        {label}{sort.key === key && (sort.direction === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </button>
      {definition && <span id={`campaign-definition-${key}`} role="tooltip" className={`pointer-events-none invisible absolute top-full z-30 mt-2 w-64 rounded-md border bg-popover px-3 py-2 text-left text-xs font-normal leading-snug whitespace-normal text-popover-foreground opacity-0 shadow-md transition-opacity group-hover/definition:visible group-hover/definition:opacity-100 group-focus-within/definition:visible group-focus-within/definition:opacity-100 ${align === 'right' ? 'right-0' : 'left-0'}`}>{definition}</span>}
    </span>
  </th>

  const cells = (row, level) => visibleColumns.map(column => <MetricCell key={column.key} row={row} column={column} level={level} adQuality={adQuality} pending={pending} />)

  return <Card>
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Campaign Breakdown</CardTitle>
          <CardDescription>
            Campaign → Adset → Ad hierarchy &middot; {tree.summary.campaign_count} campaigns, {tree.summary.adset_count} adsets
            {supportsModels ? ' · attributed sales use the Channels report’s engine' : ''}
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Channel" value={channelFilter} onChange={event => { setChannelFilter(event.target.value); setExpanded(new Set()) }} className="h-8 rounded-md border bg-background px-2 text-xs text-foreground">
            <option value="">All Channels</option>
            {channels.map(channel => <option key={channel} value={channel}>{channelLabel(channel)}</option>)}
          </select>
          <ChannelViewPicker report="campaigns" normalize={normalizeCampaignColumns} preferences={preferences} activeId={activeViewId}
            onApply={(layout, viewId) => { setPreferences(layout); setActiveViewId(viewId) }} />
          <ChannelColumnPicker catalog={catalog} defaults={DEFAULT_CAMPAIGN_COLUMNS.filter(key => catalog.some(column => column.key === key))}
            pinned="Name" preferences={preferences} onChange={setPreferences} />
        </div>
      </div>
      {supportsModels && <div className="mt-3 flex flex-wrap items-end gap-3 rounded-md border bg-muted/20 px-3 py-3">
        <label className={fieldClass}>Attribution engine
          <select className={controlClass} value={engine} onChange={event => setEngine(event.target.value)}>
            {models.map(model => <option key={model.id} value={model.id}>{model.label}</option>)}
          </select>
        </label>
        <label className={fieldClass}>Subscription filter<select className={controlClass} value={scope} onChange={event => setScope(event.target.value)}><option value="new">New orders</option><option value="all">All paid orders</option></select></label>
        <label className={fieldClass}>Accounting mode<select className={controlClass} value={accountingMode} onChange={event => setAccountingMode(event.target.value)}><option value="payment">Event date</option><option value="touchpoint">Click date</option></select></label>
        <AttributionWindowControl value={attributionWindow} onChange={setAttributionWindow} />
      </div>}
    </CardHeader>
    <CardContent className="space-y-3">
      {pending && <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Attributing sales to campaigns…</p>}
      {credit.error && <p role="alert" className="text-xs text-destructive">Attributed sales unavailable: {credit.error}</p>}
      {credit.data && <p className="text-xs text-muted-foreground">
        {placedShare == null ? 'No attributed sales in these dates.' : <>{placedShare.toFixed(1)}% of {credit.data.model.label} credit is placed on a campaign; the rest is listed under the campaigns by channel.</>}
        {' '}Credit goes as deep as a click’s UTMs prove: Google auto-tagging names the campaign only, so its credit sits on “Credited to the campaign only”.
      </p>}
      {overlapping && <p className="text-xs text-amber-700 dark:text-amber-400">Multi / Triple credit overlaps across channels and campaigns and can exceed actual purchases.</p>}
      {credit.data?.bigquery_touches && !credit.data.bigquery_touches.checked && <p className="text-xs text-amber-700 dark:text-amber-400">BigQuery clicks were not added ({credit.data.bigquery_touches.reason}), so journeys show only what core recorded.</p>}

      {/* Phones: a card per campaign, adsets and ads nested inside. */}
      <div className="space-y-2 md:hidden">
        <div className="flex items-center gap-2">
          <label className="flex min-w-0 flex-1 items-center gap-2 text-xs text-muted-foreground">Sort by
            <select aria-label="Sort by" value={sort.key} onChange={event => setSort(previous => ({ ...previous, key: event.target.value }))} className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs text-foreground">
              <option value="name">Name</option>
              <option value="channel">Channel</option>
              {visibleColumns.map(column => <option key={column.key} value={column.key}>{column.label}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => setSort(previous => ({ ...previous, direction: previous.direction === 'desc' ? 'asc' : 'desc' }))}
            aria-label={sort.direction === 'desc' ? 'Sorted high to low; switch to low to high' : 'Sorted low to high; switch to high to low'}
            className="inline-flex h-8 items-center gap-1 rounded-md border bg-background px-2 text-xs">
            {sort.direction === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}{sort.direction === 'desc' ? 'High–low' : 'Low–high'}
          </button>
        </div>
        {shown.map(campaign => <MobileRow key={campaign.key} row={campaign} level="campaign" channel={campaign.channel}
          name={<>{campaign.campaign_name}{campaign.noSpend && <NoSpend />}</>}
          columns={visibleColumns} adQuality={adQuality} pending={pending} childLabel={campaign.adsets.length === 1 ? 'adset' : 'adsets'}>
          {sortCampaignRows(campaign.adsets, sort).map(adset => <MobileRow key={adset.key} row={adset} level="adset"
            name={<>{adset.synthetic ? <em>{adset.adset_name}</em> : adset.adset_name}{adset.noSpend && <NoSpend />}</>}
            columns={visibleColumns} adQuality={adQuality} pending={pending} childLabel={adset.ads.length === 1 ? 'ad' : 'ads'}>
            {sortCampaignRows(adset.ads, sort).map(ad => <MobileRow key={ad.key} row={ad} level="ad"
              name={<>{ad.ad_name}{ad.noSpend && <NoSpend />}</>} columns={visibleColumns} adQuality={adQuality} pending={pending} />)}
          </MobileRow>)}
        </MobileRow>)}
        {!shown.length && <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">No campaigns for this channel in these dates.</p>}
        {unmatched.length > 0 && <>
          <h3 className="pt-2 text-xs font-medium text-muted-foreground">Credit not placed on a campaign</h3>
          {unmatched.map(row => {
            const own = ['unattributed', 'outside_window'].includes(row.channel)
            return <MobileRow key={`unmatched-${row.channel}`} row={row} level="campaign" channel={own ? null : row.channel}
              name={<span className="text-muted-foreground">{own ? ATTRIBUTION_CHANNEL_LABELS[row.channel] || channelLabel(row.channel) : 'No matching campaign'}</span>}
              columns={visibleColumns} adQuality={adQuality} pending={pending} />
          })}
        </>}
        <MobileRow row={totals} level="campaign" tone="total"
          name={<>{overlapping ? 'Total overlapping credit' : 'Total'}{channelFilter && <span className="block text-xs font-normal text-muted-foreground">{channelLabel(channelFilter)} only</span>}</>}
          columns={visibleColumns.filter(column => column.key !== 'quality' && column.key !== 'cpmr')} pending={pending} />
      </div>

      <div className="hidden md:block overflow-auto max-h-[75vh] rounded-md border">
        <table className="w-full text-sm tabular-nums">
          <caption className="sr-only">Campaign, adset and ad spend{supportsModels ? ' with attributed sales' : ''}</caption>
          <thead className="sticky top-0 z-20"><tr>
            {header('name', 'Name', 'left')}
            {header('channel', 'Channel', 'left')}
            {visibleColumns.map(column => header(column.key, column.label, 'right', column.description))}
          </tr></thead>
          <tbody>
            {shown.map(campaign => {
              const open = expanded.has(campaign.key)
              return [
                <tr key={campaign.key} className="group border-t hover:bg-muted/30">
                  <NameCell depth={0} expandable={campaign.adsets.length > 0} expanded={open} onToggle={() => toggle(campaign.key)}>
                    {campaign.campaign_name}
                    {campaign.noSpend && <NoSpend />}
                  </NameCell>
                  <ChannelCell channel={campaign.channel} />
                  {cells(campaign, 'campaign')}
                </tr>,
                ...(open ? sortCampaignRows(campaign.adsets, sort).flatMap(adset => {
                  const adsetKey = `${campaign.key}::${adset.key}`
                  const adsetOpen = expanded.has(adsetKey)
                  return [
                    <tr key={adsetKey} className="group border-t bg-muted/10 hover:bg-muted/20">
                      <NameCell depth={1} expandable={adset.ads.length > 0} expanded={adsetOpen} onToggle={() => toggle(adsetKey)} muted>
                        {adset.synthetic ? <em>{adset.adset_name}</em> : adset.adset_name}
                        {adset.noSpend && <NoSpend />}
                      </NameCell>
                      <td />
                      {cells(adset, 'adset')}
                    </tr>,
                    ...(adsetOpen ? sortCampaignRows(adset.ads, sort).map(ad => <tr key={`${adsetKey}::${ad.key}`} className="group border-t bg-muted/5 hover:bg-muted/15">
                      <NameCell depth={2} muted>{ad.ad_name}{ad.noSpend && <NoSpend />}</NameCell>
                      <td />
                      {cells(ad, 'ad')}
                    </tr>) : []),
                  ]
                }) : []),
              ]
            })}
            {!shown.length && <tr><td colSpan={visibleColumns.length + 2} className="p-10 text-center text-muted-foreground">No campaigns for this channel in these dates.</td></tr>}
          </tbody>
          {unmatched.length > 0 && <tbody className="border-t-2">
            <tr><th scope="rowgroup" colSpan={visibleColumns.length + 2} className="bg-muted/30 px-3 py-2 text-left text-xs font-medium text-muted-foreground">Credit not placed on a campaign</th></tr>
            {unmatched.map(row => <tr key={`unmatched-${row.channel}`} className="group border-t hover:bg-muted/30">
              <NameCell depth={0} muted>
                <span title={['unattributed', 'outside_window'].includes(row.channel) ? undefined : 'Credit to this channel whose click carried no UTMs matching a campaign in ad spend.'}>
                  {['unattributed', 'outside_window'].includes(row.channel) ? ATTRIBUTION_CHANNEL_LABELS[row.channel] || channelLabel(row.channel) : 'No matching campaign'}
                </span>
              </NameCell>
              <ChannelCell channel={['unattributed', 'outside_window'].includes(row.channel) ? null : row.channel} />
              {cells(row, 'campaign')}
            </tr>)}
          </tbody>}
          <tfoot className="border-t-2 bg-muted/40">
            <tr>
              <th scope="row" className="sticky left-0 z-10 bg-muted px-3 py-2.5 text-left font-semibold">
                {overlapping ? 'Total overlapping credit' : 'Total'}{channelFilter && <span className="block text-xs font-normal text-muted-foreground">{channelLabel(channelFilter)} only</span>}
              </th>
              <td />
              {visibleColumns.map(column => column.key === 'quality' || column.key === 'cpmr'
                ? <td key={column.key} />
                : <MetricCell key={column.key} row={totals} column={column} level="campaign" pending={pending} />)}
            </tr>
          </tfoot>
        </table>
      </div>

      {supportsModels && credit.data && <details className="rounded-md border p-4 text-sm">
        <summary className="cursor-pointer font-medium">How attributed sales are placed on campaigns</summary>
        <div className="mt-4 space-y-3 text-muted-foreground">
          <div className="flex flex-wrap gap-2"><AttributionModelDetails model={credit.data.model} scope={scope} lookbackDays={lookbackDays} attributionWindow={attributionWindow} accountingMode={accountingMode} /></div>
          <p>The engine is the Channels report’s: the same paid orders, journeys, lookback and attribution window. A channel’s campaigns plus its “no matching campaign” row add up to that channel’s row on Channels.</p>
          <p>Each credited click is matched to ad spend by its UTMs. Ids match first (campaign id, adset id, ad id), then names within the click’s own channel. A name shared by two ads matches neither, so that credit stays on the adset or campaign rather than guessing. A row’s figures include its adsets and ads.</p>
          <p>Rows marked “no spend in dates” were credited from a click inside the lookback on an ad that did not spend in these dates. CAC and ROAS are unavailable without spend.</p>
          <AttributionWindowExplanation />
        </div>
      </details>}
    </CardContent>
  </Card>
}
